import React, { useState, useEffect } from 'react';
import api from '../../api/client';
import { CHART_COLORS, CHART_THEME } from './constants';
import StatCard, { STAT_ICONS } from '../../components/ui/StatCard';
import PanelCard from '../../components/ui/PanelCard';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';

const UNIQUE_COLOR = '#38bdf8';

export default function TrafficTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get('/stats/traffic')
      .then(res => setData(res.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  if (loading) return <div className="py-12 flex justify-center"><LoadingSpinner label="Loading traffic…" /></div>;
  if (!data) return <p className="text-center text-gray-500 py-12">Failed to load traffic data.</p>;

  const chartData = data.viewsByDay.map(d => ({
    day: d.day?.slice(5, 10),
    views: d.views,
    unique: d.unique_visitors,
  }));

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <button onClick={load} className="btn-secondary text-sm py-2">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
               strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
            <path d="M21 12a9 9 0 11-2.6-6.4M21 3v6h-6" />
          </svg>
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <StatCard label="Today's Visitors" value={data.todayUnique}   icon={STAT_ICONS.eye}   tone="brand" />
        <StatCard label="Today's Views"    value={data.todayViews}    icon={STAT_ICONS.page} />
        <StatCard label="Views (14 days)"  value={data.totalViews14d} icon={STAT_ICONS.chart} />
      </div>

      <PanelCard title="Site Traffic — Last 14 Days" subtitle="Page views and unique visitors per day.">
        <ResponsiveContainer width="100%" height={240}>
          <AreaChart data={chartData} margin={{ top: 6, right: 6, left: -18, bottom: 0 }}>
            <defs>
              <linearGradient id="viewsGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={CHART_COLORS[0]} stopOpacity={0.4} />
                <stop offset="100%" stopColor={CHART_COLORS[0]} stopOpacity={0} />
              </linearGradient>
              <linearGradient id="uniqueGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={UNIQUE_COLOR} stopOpacity={0.28} />
                <stop offset="100%" stopColor={UNIQUE_COLOR} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={CHART_THEME.grid} vertical={false} />
            <XAxis dataKey="day" stroke={CHART_THEME.axis} fontSize={11} tickLine={false} axisLine={false} />
            <YAxis stroke={CHART_THEME.axis} fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
            <Tooltip
              cursor={{ stroke: CHART_COLORS[0], strokeOpacity: 0.3 }}
              contentStyle={CHART_THEME.tooltip}
              labelStyle={CHART_THEME.tooltipLabel}
            />
            <Legend wrapperStyle={{ fontSize: 12, color: '#9c96af', paddingTop: 8 }} />
            <Area type="monotone" dataKey="views"  name="Views"           stroke={CHART_COLORS[0]} strokeWidth={2} fill="url(#viewsGrad)" />
            <Area type="monotone" dataKey="unique" name="Unique Visitors" stroke={UNIQUE_COLOR}    strokeWidth={2} fill="url(#uniqueGrad)" />
          </AreaChart>
        </ResponsiveContainer>
      </PanelCard>

      <PanelCard title="Top Pages (14 days)" subtitle="Most visited pages on the site.">
        {data.topPages.length === 0 ? (
          <p className="text-center text-gray-500 text-sm py-8">No data yet.</p>
        ) : (
          <div className="space-y-2.5">
            {data.topPages.map((p, i) => {
              const max = data.topPages[0].views || 1;
              return (
                <div key={p.page} className="flex items-center gap-3">
                  <span className="text-xs text-gray-600 w-5 text-right tabular-nums">{i + 1}</span>
                  <span className="text-sm text-gray-300 w-32 truncate shrink-0 font-mono text-[13px]">
                    {p.page}
                  </span>
                  <div className="flex-1 h-2 rounded-full bg-white/[0.05] overflow-hidden">
                    <div
                      className="h-2 rounded-full transition-all duration-700 ease-smooth"
                      style={{
                        width: `${(p.views / max) * 100}%`,
                        background: `linear-gradient(90deg, ${CHART_COLORS[2]}, ${CHART_COLORS[0]})`,
                      }}
                    />
                  </div>
                  <span className="text-xs text-gray-500 w-16 text-right tabular-nums">{p.views} views</span>
                </div>
              );
            })}
          </div>
        )}
      </PanelCard>
    </div>
  );
}
